import * as THREE from 'three';
import { POI } from '../poi/POI';
import { Logger } from '../core/Logger';

/**
 * Ein Annotationspunkt aus dem Knowledge-System (/annotated/{id}).
 * anchor: 2D-Bildkoordinaten (0-1, y von oben) — fuer Flaechen-Shapes.
 * position3D: normierter Modellraum (laengste Achse = 2, Zentrum 0) — fuer GLTF.
 */
export interface KnowledgeAnnotation {
    id: string;
    label: string;
    description: string;
    anchor: { x: number; y: number } | null;
    position3D: { x: number; y: number; z: number } | null;
}

/**
 * AnnotationOverlay — MeshPoints auf Basis des Knowledge-Annotationssystems.
 *
 * Das alte MeshPoint-System (labelMeshPoint/MeshPointIndicator) war ein frueher
 * Entwurf derselben Idee, wurde aber nie produktiv. Die kanonische Datenquelle
 * ist heute das Knowledge-Admin-Portal: pro Objekt annotierte Punkte mit 2D-
 * Bildanker UND 3D-Position am Modell. Dieses Overlay laedt die Punkte fuer die
 * AKTIVE POI (poidata.knowledgeId), zeigt sie als nummerierte Marker auf der
 * Shape und liefert Weltpositionen fuer das Kamera-Hopping zwischen den Punkten.
 */
export class AnnotationOverlay {
    private static readonly API_BASE = 'https://knowledge-api.arkserver.arkturian.com/api/v1/knowledge';

    private _cache = new Map<number, KnowledgeAnnotation[]>();
    private _markerGroup: THREE.Group | null = null;
    private _markerHost: THREE.Object3D | null = null;
    private _markers: THREE.Sprite[] = [];
    private _annotations: KnowledgeAnnotation[] = [];
    private _index = -1;
    private _generation = 0;

    /** Aktive POI wechseln: Marker der alten entfernen, neue asynchron laden. */
    public async setActivePoi(poi: POI | null): Promise<void> {
        const gen = ++this._generation;
        this.clearMarkers();
        this._annotations = [];
        this._index = -1;

        const kid = Number((poi?.poidata as any)?.knowledgeId?.value ?? 0);
        if (!poi || !kid) return;

        let anns: KnowledgeAnnotation[];
        try {
            anns = await this.fetchAnnotations(kid);
        } catch (e) {
            Logger.Instance.Warn(`Annotationen fuer knowledgeId=${kid} nicht ladbar: ${e}`, 'Annotations');
            return;
        }
        if (gen !== this._generation) return; // POI hat inzwischen gewechselt
        if (!anns.length) return;

        this._annotations = anns;
        // GLTF laedt asynchron NACH dem Annotations-Fetch: eine Bounds-Messung am
        // leeren Host wuerde die Marker auf die Fallback-Skala setzen (gesehen beim
        // Buschwindroeschen: Bluete-Marker auf halber Hoehe). Warten, bis die Shape
        // echte Ausdehnung hat, dann bauen.
        const shapeKind = String(poi.poidata.shape?.value ?? '');
        const deadline = performance.now() + 30000;
        while (performance.now() < deadline) {
            const sh: any = poi.shape;
            let ready = false;
            if (shapeKind === 'gltf' || shapeKind === 'usdz') {
                const ls: THREE.Object3D | null = sh?.loadedScene ?? null;
                ready = !!(ls && ls.children.length > 0 && ls.parent);
            } else if (shapeKind === 'multiplane') {
                // Frontplane muss existieren UND ihr Bild-Seitenverhaeltnis kennen —
                // vorher waere der Anker auf ein 1:1-Quadrat gerechnet (2D-Fall, in
                // dem die Marker bei Alex gar nicht erschienen).
                const planes: any[] = sh?._planes ?? [];
                const front = planes[planes.length - 1];
                ready = !!(front?._mesh && (front.primaryMaterial?.aspectRatio?.value ?? 0) > 0);
            } else {
                ready = !!sh?._mesh;
            }
            if (ready) break;
            await new Promise(r => setTimeout(r, 300));
            if (gen !== this._generation) return;
        }
        this.buildMarkers(poi);
        Logger.Instance.Info(`${anns.length} Annotationen fuer ${poi.name} (knowledgeId=${kid})`, 'Annotations');
    }

    public get count(): number { return this._annotations.length; }
    public get index(): number { return this._index; }
    public get current(): KnowledgeAnnotation | null {
        return this._index >= 0 ? this._annotations[this._index] ?? null : null;
    }

    /**
     * Naechster Punkt. true = es gibt einen (Kamera soll hinfliegen),
     * false = Ende erreicht (Aufrufer wechselt zur naechsten POI).
     */
    public focusNext(): boolean {
        if (!this._annotations.length) return false;
        if (this._index + 1 >= this._annotations.length) { this._index = -1; return false; }
        this._index++;
        this.highlight();
        return true;
    }

    /** Voriger Punkt. false = wir stehen vor dem ersten (Aufrufer wechselt POI). */
    public focusPrev(): boolean {
        if (!this._annotations.length || this._index < 0) return false;
        this._index--;
        this.highlight();
        return this._index >= 0;
    }

    /** Direkt auf einen Punkt springen (Tap auf Marker). false = Index ungueltig. */
    public focusIndex(i: number): boolean {
        if (i < 0 || i >= this._annotations.length) return false;
        this._index = i;
        this.highlight();
        return true;
    }

    /**
     * Marker unter einem Bildschirmpunkt finden. Sprites sind fuer den POI-Raycast
     * unsichtbar (sie haengen an der Shape, aber der Presenter sucht POIs, nicht
     * Sprites) — deshalb ein eigener Test gegen die Marker-Sprites.
     */
    public hitTest(ndcX: number, ndcY: number, camera: THREE.Camera): number {
        if (!this._markers.length) return -1;
        const rc = new THREE.Raycaster();
        rc.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);
        const hits = rc.intersectObjects(this._markers.filter(Boolean), false);
        if (!hits.length) return -1;
        return this._markers.indexOf(hits[0].object as THREE.Sprite);
    }

    /** Weltposition des aktuellen Punkts (fuer den Kamera-Fokus). */
    public currentWorldPosition(): THREE.Vector3 | null {
        if (this._index < 0 || !this._markers[this._index] || !this._markerGroup) return null;
        const home: THREE.Vector3 | undefined = this._markers[this._index].userData.home;
        if (!home) {
            const v = new THREE.Vector3();
            this._markers[this._index].getWorldPosition(v);
            return v;
        }
        this._markerGroup.updateWorldMatrix(true, false);
        return this._markerGroup.localToWorld(home.clone());
    }

    /**
     * Marker im Bildraum entzerren (#1759). Zwei Anker, die auf dem Bildschirm
     * naeher beieinander liegen als ihre Ziffernkreise, werden auseinandergeschoben
     * (der aktive Marker bleibt liegen); die Reihenfolge der Nummern bleibt die des
     * Knowledge-Portals. Verschobene Marker bekommen eine Leitlinie zum Anker.
     * Jeden Frame aufrufen — die Loesung ist eine stetige Funktion der Projektion,
     * springt also bei Kamerafahrten nicht.
     */
    public declutter(camera: THREE.Camera, viewportWidth: number, viewportHeight: number): void {
        const group = this._markerGroup;
        if (!group || viewportWidth <= 0 || viewportHeight <= 0) return;
        const sprites = this._markers.filter((m): m is THREE.Sprite => Boolean(m));
        if (sprites.length < 2) return;
        group.updateWorldMatrix(true, false);
        const groupScale = new THREE.Vector3();
        group.matrixWorld.decompose(new THREE.Vector3(), new THREE.Quaternion(), groupScale);
        const right = new THREE.Vector3(1, 0, 0).applyQuaternion(camera.quaternion);

        interface Entry { sprite: THREE.Sprite; active: boolean; z: number; hx: number; hy: number; px: number; py: number; r: number }
        const entries: Entry[] = [];
        for (const sprite of sprites) {
            const home: THREE.Vector3 | undefined = sprite.userData.home;
            if (!home) continue;
            const world = group.localToWorld(home.clone());
            const ndc = world.clone().project(camera);
            if (ndc.z > 1 || ndc.z < -1) continue; // hinter der Kamera / ausserhalb der Tiefe
            const px = (ndc.x + 1) / 2 * viewportWidth;
            const py = (1 - ndc.y) / 2 * viewportHeight;
            const halfWorld = sprite.scale.x * groupScale.x / 2;
            const edge = world.clone().addScaledVector(right, halfWorld).project(camera);
            const r = Math.max(6, Math.abs(edge.x - ndc.x) / 2 * viewportWidth);
            entries.push({ sprite, active: this._markers.indexOf(sprite) === this._index, z: ndc.z, hx: px, hy: py, px, py, r });
        }

        const pad = 3;
        for (let iter = 0; iter < 6; iter++) {
            let moved = false;
            for (let a = 0; a < entries.length; a++) {
                for (let b = a + 1; b < entries.length; b++) {
                    const A = entries[a], B = entries[b];
                    let dx = B.px - A.px, dy = B.py - A.py;
                    let d = Math.hypot(dx, dy);
                    const min = A.r + B.r + pad;
                    if (d >= min) continue;
                    if (d < 1e-3) { dx = 0; dy = 1; d = 1; } // deckungsgleich: deterministisch nach unten
                    const ux = dx / d, uy = dy / d;
                    const push = min - d;
                    const wA = A.active ? 0 : (B.active ? 1 : 0.5);
                    const wB = B.active ? 0 : (A.active ? 1 : 0.5);
                    A.px -= ux * push * wA; A.py -= uy * push * wA;
                    B.px += ux * push * wB; B.py += uy * push * wB;
                    moved = true;
                }
            }
            if (!moved) break;
        }

        for (const e of entries) {
            // Nie weiter als drei Radien vom Anker weg, sonst verliert die Ziffer ihren Bezug.
            const maxShift = e.r * 3;
            let sx = e.px - e.hx, sy = e.py - e.hy;
            const shift = Math.hypot(sx, sy);
            if (shift > maxShift) { sx *= maxShift / shift; sy *= maxShift / shift; }
            const leader: THREE.Line | undefined = e.sprite.userData.leader;
            const home: THREE.Vector3 = e.sprite.userData.home;
            if (shift < 0.5) {
                e.sprite.position.copy(home);
                if (leader) leader.visible = false;
                continue;
            }
            const ndc = new THREE.Vector3(((e.hx + sx) / viewportWidth) * 2 - 1, 1 - ((e.hy + sy) / viewportHeight) * 2, e.z);
            const local = group.worldToLocal(ndc.unproject(camera));
            e.sprite.position.copy(local);
            if (leader) {
                leader.visible = shift > e.r * 0.6;
                if (leader.visible) {
                    const pos = leader.geometry.getAttribute('position') as THREE.BufferAttribute;
                    pos.setXYZ(0, home.x, home.y, home.z);
                    pos.setXYZ(1, local.x, local.y, local.z);
                    pos.needsUpdate = true;
                }
            }
        }
    }

    private async fetchAnnotations(kid: number): Promise<KnowledgeAnnotation[]> {
        const hit = this._cache.get(kid);
        if (hit) return hit;
        const res = await fetch(`${AnnotationOverlay.API_BASE}/annotated/${kid}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        // Reihenfolge wie im Knowledge-Portal (Array-Reihenfolge) — nicht umsortieren,
        // sonst weichen die Nummern von dem ab, was Alex im Admin sieht.
        const anns: KnowledgeAnnotation[] = (data?.annotations ?? []).map((a: any) => ({
            id: String(a.id ?? ''),
            label: String(a.label ?? ''),
            description: String(a.description ?? ''),
            anchor: a.anchor && typeof a.anchor.x === 'number' ? { x: a.anchor.x, y: a.anchor.y } : null,
            position3D: a.position3D && typeof a.position3D.x === 'number'
                ? { x: a.position3D.x, y: a.position3D.y, z: a.position3D.z } : null,
        }));
        this._cache.set(kid, anns);
        return anns;
    }

    // ---------- Marker ----------

    private buildMarkers(poi: POI): void {
        const shape: any = poi.shape;
        if (!shape) return;
        const shapeType = String(poi.poidata.shape?.value ?? '');

        let host: THREE.Object3D | null = null;
        let place: (a: KnowledgeAnnotation) => THREE.Vector3 | null = () => null;

        if (shapeType === 'multiplane' && Array.isArray(shape._planes) && shape._planes.length) {
            // Vorderste Plane (letzte im Stapel) traegt das Bild — Anker 1:1 abbildbar.
            const plane: any = shape._planes[shape._planes.length - 1];
            host = plane?._mesh ?? null;
            const aspect = Number(plane?.primaryMaterial?.aspectRatio?.value ?? 1) || 1;
            place = a => a.anchor
                ? new THREE.Vector3((a.anchor.x - 0.5) * aspect, (0.5 - a.anchor.y) * 1.0, 0.02)
                : null;
        } else if (shapeType === 'gltf' || shapeType === 'usdz') {
            // Direkt an der loadedScene arbeiten: shape._mesh wird von GLTFShape
            // nach dem asynchronen Laden NEU aufgebaut — eine frueher angehaengte
            // Marker-Gruppe lebte dann in einem verwaisten Objekt weiter (gemessen:
            // markerHost.children leer, Weltpositionen wirr). Die loadedScene bleibt
            // nach dem Laden stabil.
            host = shape.loadedScene ?? null;
            if (host) {
                // position3D ist auf laengste Achse = 2 normiert, Zentrum = Modellmitte.
                // Bounds im LOKALRAUM der loadedScene — und NUR ueber sichtbare
                // Meshes: die Blender-Pipeline laesst unsichtbare Hilfs-Meshes im
                // GLB zurueck (gemessen am Buschwindroeschen: ein visible:false-Mesh
                // am Ursprung), die setFromObject mitzaehlt und damit Zentrum und
                // Skala der Marker verfaelscht.
                // Bounds NICHT ueber matrixWorld rechnen: die haengt am Zeitpunkt
                // (gltfNormalize/Layouter-Scale koennen zwischen Messung und Marker-
                // Setzen greifen — gemessen: Host-Scale 0.524, Marker auf 1.9er-Skala,
                // Punkte ueber die halbe Wabenkugel verstreut). Stattdessen die
                // Geometrie-Bounds jedes sichtbaren Meshes ueber die Kette der
                // LOKALEN Matrizen bis zum Host projizieren — das ist der Raum, in
                // dem die Marker als Kinder des Hosts leben, unabhaengig von dessen
                // eigener Skala.
                const box = new THREE.Box3();
                host.traverse((o: any) => {
                    if (!o.isMesh || !o.visible || !o.geometry) return;
                    if (!o.geometry.boundingBox) o.geometry.computeBoundingBox();
                    const gb: THREE.Box3 = o.geometry.boundingBox.clone();
                    // Kette der lokalen Matrizen von o bis (exklusive) host
                    const m = new THREE.Matrix4();
                    let cur: THREE.Object3D | null = o;
                    while (cur && cur !== host) {
                        cur.updateMatrix();
                        m.premultiply(cur.matrix);
                        cur = cur.parent;
                    }
                    gb.applyMatrix4(m);
                    if (!gb.isEmpty()) box.union(gb);
                });
                if (box.isEmpty()) box.set(new THREE.Vector3(-0.5, -0.5, -0.5), new THREE.Vector3(0.5, 0.5, 0.5));
                const size = box.getSize(new THREE.Vector3());
                const center = box.getCenter(new THREE.Vector3());
                const half = Math.max(size.x, size.y, size.z) / 2 || 1;
                place = a => a.position3D
                    ? new THREE.Vector3(
                        center.x + a.position3D.x * half,
                        center.y + a.position3D.y * half,
                        center.z + a.position3D.z * half)
                    : null;
            }
        } else if (shapeType === 'hexagon' || shapeType === 'tile' || shapeType === 'plane') {
            host = shape._mesh ?? null;
            const aspect = Number(shape?.primaryMaterial?.aspectRatio?.value ?? 1) || 1;
            const fit = String(poi.poidata.textureFit?.value ?? 'cover');
            const depth = Number(poi.poidata.depth?.value ?? 0.1);
            if (shapeType === 'hexagon') {
                const size = 0.5;
                if (fit === 'contain') {
                    // Spiegel der contain-UV-Mathe in ThreeGeomFactory.createRoundedHexagon
                    const S3 = Math.sqrt(3);
                    const h = Math.min((2 * S3 * size) / (1 + S3 * aspect), S3 * size);
                    const w = aspect * h;
                    place = a => a.anchor
                        ? new THREE.Vector3((a.anchor.x - 0.5) * w, (0.5 - a.anchor.y) * h, depth + 0.02)
                        : null;
                } else {
                    // cover: Bild fuellt den Umkreis, zugeschnitten je Seitenverhaeltnis
                    const crop = aspect > 1 ? 1 / aspect : 1;
                    const cropV = aspect < 1 ? aspect : 1;
                    place = a => {
                        if (!a.anchor) return null;
                        const u = aspect > 1 ? (a.anchor.x - (1 - crop) / 2) / crop : a.anchor.x;
                        const v = aspect < 1 ? (a.anchor.y - (1 - cropV) / 2) / cropV : a.anchor.y;
                        return new THREE.Vector3((u - 0.5) * 2 * size, (0.5 - v) * 2 * size, depth + 0.02);
                    };
                }
            } else {
                place = a => a.anchor
                    ? new THREE.Vector3((a.anchor.x - 0.5) * aspect, (0.5 - a.anchor.y) * 1.0, depth + 0.02)
                    : null;
            }
        }

        if (!host) return;
        const group = new THREE.Group();
        group.name = 'AnnotationMarkers';
        this._annotations.forEach((a, i) => {
            const pos = place(a);
            if (!pos) return;
            const sprite = this.makeMarkerSprite(String(i + 1));
            sprite.position.copy(pos);
            // Datenposition merken: declutter() verschiebt das Sprite im Bildraum,
            // Kamera-Fokus und Leitlinie beziehen sich weiter auf den Anker.
            sprite.userData.home = pos.clone();
            const leader = new THREE.Line(
                new THREE.BufferGeometry().setFromPoints([pos.clone(), pos.clone()]),
                new THREE.LineBasicMaterial({ color: 0xf3ead2, transparent: true, opacity: 0.55, depthTest: false }),
            );
            leader.renderOrder = 1199;
            leader.visible = false;
            leader.name = 'AnnotationLeader';
            sprite.userData.leader = leader;
            group.add(leader);
            group.add(sprite);
            this._markers[i] = sprite;
        });
        host.add(group);
        this._markerGroup = group;
        this._markerHost = host;
        this.highlight();
    }

    private makeMarkerSprite(text: string): THREE.Sprite {
        const dpr = 3;
        const s = 44 * dpr;
        const canvas = document.createElement('canvas');
        canvas.width = s; canvas.height = s;
        const ctx = canvas.getContext('2d')!;
        ctx.beginPath();
        ctx.arc(s / 2, s / 2, s / 2 - 3 * dpr, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(13, 23, 16, 0.78)';
        ctx.fill();
        ctx.lineWidth = 2.5 * dpr;
        ctx.strokeStyle = 'rgba(243, 234, 210, 0.95)';
        ctx.stroke();
        ctx.fillStyle = 'rgba(243, 234, 210, 1)';
        ctx.font = `600 ${20 * dpr}px Georgia, serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'alphabetic';
        // Optisch zentrieren ueber die echte Glyphenbox — 'middle' setzt Ziffern in
        // Georgia sichtbar zu tief (Befund Alex #119765).
        const m = ctx.measureText(text);
        const asc = m.actualBoundingBoxAscent || 14 * dpr;
        const desc = m.actualBoundingBoxDescent || 0;
        ctx.fillText(text, s / 2, s / 2 + (asc - desc) / 2);
        const tex = new THREE.CanvasTexture(canvas);
        tex.colorSpace = THREE.SRGBColorSpace;
        const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false });
        const sprite = new THREE.Sprite(mat);
        sprite.scale.set(0.12, 0.12, 1);
        sprite.renderOrder = 1200;
        return sprite;
    }

    /** Aktiven Marker hervorheben (groesser + voll deckend, Rest gedimmt). */
    private highlight(): void {
        this._markers.forEach((m, i) => {
            if (!m) return;
            const active = i === this._index;
            m.scale.set(active ? 0.19 : 0.12, active ? 0.19 : 0.12, 1);
            (m.material as THREE.SpriteMaterial).opacity = this._index < 0 ? 1 : (active ? 1 : 0.45);
        });
    }

    private clearMarkers(): void {
        if (this._markerGroup) {
            this._markerHost?.remove(this._markerGroup);
            this._markerGroup.traverse((o: any) => {
                o.material?.map?.dispose?.();
                o.material?.dispose?.();
            });
        }
        this._markerGroup = null;
        this._markerHost = null;
        this._markers = [];
    }
}
